// @ts-nocheck
import React from 'react';

/* ═══════════════════════════════════════════
   Dot9loader — Standardized loader for ThetaRetail
   ═══════════════════════════════════════════
   Usage:
     <Dot9loader />                          — centered spinner + "Loading…"
     <Dot9loader message="Loading orders…" /> — custom message
     <Dot9loader overlay />                   — absolute overlay with semi-transparent bg
     <Dot9loader compact />                   — small inline spinner, no text
*/

const isDark = () => typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';

const baseStyle: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	height: '100%',
	minHeight: 120,
	flexDirection: 'column',
	gap: 10,
};

const compactStyle: React.CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 8,
};

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('theta-spin-kf')) {
	const style = document.createElement('style');
	style.id = 'theta-spin-kf';
	style.textContent = `@keyframes theta-spin { to { transform: rotate(360deg); } }`;
	document.head.appendChild(style);
}

interface Dot9loaderProps {
	message?: string;
	overlay?: boolean;
	compact?: boolean;
}

export const Dot9loader: React.FC<Dot9loaderProps> = ({ message = 'Loading…', overlay = false, compact = false }) => {
	const dark = isDark();

	const overlayStyle: React.CSSProperties = {
		...baseStyle,
		position: 'absolute',
		inset: 0,
		zIndex: 500,
		background: dark ? 'rgba(26, 29, 35, 0.92)' : 'rgba(255, 255, 255, 0.92)',
		minHeight: 0,
	};

	const ringStyle: React.CSSProperties = {
		width: 36,
		height: 36,
		border: `3px solid ${dark ? '#3a3f4a' : '#e0e0e8'}`,
		borderTopColor: dark ? '#7b8adf' : '#5b6abf',
		borderRadius: '50%',
		animation: 'theta-spin 0.7s linear infinite',
	};

	const ringCompactStyle: React.CSSProperties = {
		...ringStyle,
		width: 18,
		height: 18,
		border: `2px solid ${dark ? '#3a3f4a' : '#e0e0e8'}`,
		borderTopColor: dark ? '#7b8adf' : '#5b6abf',
	};

	const textStyle: React.CSSProperties = {
		fontSize: 12,
		fontWeight: 500,
		color: dark ? '#9ea3ad' : '#888',
		fontFamily: 'system-ui, -apple-system, sans-serif',
		letterSpacing: 0.3,
	};

	if (compact) {
		return (
			<span style={compactStyle}>
				<span style={ringCompactStyle} />
				{message !== 'Loading…' && <span style={{ ...textStyle, fontSize: 11 }}>{message}</span>}
			</span>
		);
	}

	return (
		<div style={overlay ? overlayStyle : baseStyle}>
			<div style={ringStyle} />
			<div style={textStyle}>{message}</div>
		</div>
	);
};

export default Dot9loader;
